import { useFormContext, useWatch } from 'react-hook-form';
import { ConditionalRender } from '@ui';
import { Hub } from '@/api/apis/facilities';
import { EmployeeFormValues } from '@/types';
import { TrackedLocation, EmployeeRole } from '@/api/apis/resources';
import {
  ClinicalCoordinatorSpecificationSection,
  ClinicalSpecialistSpecificationSection,
  LocationInformationSection,
  RetrievalSurgeonSpecificationSection,
  RoleSpecificationSection,
  UniformMeasurementsSection,
  UserDetailsSection,
} from './components/sections';

type EmployeeFormProps = {
  isContractor?: boolean;
  isReadOnly?: boolean;
  trackedLocation?: TrackedLocation;
  baseLocation?: Hub;
};

export const EmployeeForm = ({ isContractor, isReadOnly, trackedLocation, baseLocation }: EmployeeFormProps) => {
  const { control } = useFormContext<EmployeeFormValues>();
  const roles = useWatch({ control, name: 'roles' });

  const hasRole = (role: EmployeeRole) => roles?.includes(role);

  const showClinicalSpecialist = hasRole(EmployeeRole.ClinicalSpecialist);
  const showRetrievalSurgeon = hasRole(EmployeeRole.RetrievalSurgeon);
  const showClinicalCoordinator = hasRole(EmployeeRole.ClinicalCoordinator);
  const showUniformMeasurements = showClinicalSpecialist || showRetrievalSurgeon;

  return (
    <>
      <UserDetailsSection isReadOnly={isReadOnly} isContractor={isContractor} />
      <RoleSpecificationSection isReadOnly={isReadOnly} />
      <ConditionalRender show={showUniformMeasurements}>
        <UniformMeasurementsSection isReadOnly={isReadOnly} />
      </ConditionalRender>
      <LocationInformationSection
        isReadOnly={isReadOnly}
        trackedLocation={trackedLocation}
        baseLocation={baseLocation}
      />
      <ConditionalRender show={showClinicalSpecialist}>
        <ClinicalSpecialistSpecificationSection isReadOnly={isReadOnly} />
      </ConditionalRender>
      <ConditionalRender show={showRetrievalSurgeon}>
        <RetrievalSurgeonSpecificationSection isReadOnly={isReadOnly} />
      </ConditionalRender>
      <ConditionalRender show={showClinicalCoordinator}>
        <ClinicalCoordinatorSpecificationSection isReadOnly={isReadOnly} />
      </ConditionalRender>
    </>
  );
};
